import {useCallback, useEffect, useMemo, useState} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import {
  getSearchParamFromUrl,
  Paginated,
  SelectOption,
} from '@chekin/core';

const DEFAULT_SEARCH_DEBOUNCE_MS = 500;

type UseSearchableSelectQueryParams<T, V extends string | number, L> = {
  queryKey: readonly unknown[];
  queryFn: (params: {
    page: number;
    searchQuery: string;
  }) => Promise<Paginated<SelectOption<T, V, L>>>;
  value?: SelectOption<T, V, L> | null;
  enabled?: boolean;
  resetKey?: unknown;
  debounceMs?: number;
  minSearchQueryLength?: number;
};

function getMergedOptions<T, V extends string | number, L>(
  options: SelectOption<T, V, L>[],
  value?: SelectOption<T, V, L> | null,
) {
  if (!value) return options;

  const hasSelectedOption = options.some(option => option.value === value.value);

  return hasSelectedOption ? options : [value, ...options];
}

function useSearchableSelectQuery<T = undefined, V extends string | number = string, L = string>({
  queryKey,
  queryFn,
  value,
  enabled = true,
  resetKey,
  debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  minSearchQueryLength = 0,
}: UseSearchableSelectQueryParams<T, V, L>) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchValue.trim());
    }, debounceMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [debounceMs, searchValue]);

  useEffect(() => {
    setSearchValue('');
    setDebouncedSearchQuery('');
    setHasOpenedOnce(false);
  }, [resetKey]);

  const canSearch =
    debouncedSearchQuery.length === 0 ||
    debouncedSearchQuery.length >= minSearchQueryLength;
  const queryEnabled = enabled && canSearch && (isOpen || hasOpenedOnce);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery<Paginated<SelectOption<T, V, L>>, Error>({
    queryKey: [...queryKey, debouncedSearchQuery],
    queryFn: ({pageParam = 1}) =>
      queryFn({page: pageParam as number, searchQuery: debouncedSearchQuery}),
    initialPageParam: 1,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: queryEnabled,
    getNextPageParam: lastPage => {
      if (lastPage.next) {
        return Number(getSearchParamFromUrl('page', lastPage.next));
      }
    },
  });

  const options = useMemo(() => {
    const pageOptions = data?.pages.flatMap(page => page.results) ?? [];

    return getMergedOptions(pageOptions, value);
  }, [data?.pages, value]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);

    if (nextOpen) {
      setHasOpenedOnce(true);
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    options,
    searchValue,
    setSearchValue,
    onOpenChange: handleOpenChange,
    loading: isFetching && !isFetchingNextPage,
    hasNextPage,
    onLoadMore: handleLoadMore,
    canSearch,
    error,
  };
}

export {useSearchableSelectQuery};
export type {UseSearchableSelectQueryParams};
